import React, { useCallback, type ReactNode } from 'react';
import type { TriggerProps, ContentProps } from '@atlaskit/popup';
import type { CascadingSelectState } from '@atlassian/jira-jql-builder-basic-cascading-select/src/common/types.tsx';
import {
	CascadingSelectView,
	type OnChange,
} from '@atlassian/jira-jql-builder-basic-cascading-select/src/ui/index.tsx';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { useShouldRenderToParent } from '../../../../controllers/feature-context.tsx';

type Props = {
	fieldId: string;
	initialValue?: CascadingSelectState;
	isOpen: boolean;
	onClose: () => void;
	onChange: OnChange;
	renderTrigger: (triggerProps: TriggerProps) => ReactNode;
	contextJql: string;
};

export const CascadeSelectPickerPopup = ({
	fieldId,
	initialValue,
	isOpen,
	onClose,
	onChange,
	renderTrigger,
	contextJql,
}: Props): React.JSX.Element => {
	const shouldRenderToParent = useShouldRenderToParent();
	const renderContent = useCallback(
		({ setInitialFocusRef }: ContentProps) => (
			<CascadingSelectView
				jqlTerm={fieldId}
				jqlContext={contextJql}
				initialValue={initialValue}
				onChange={onChange}
				setInitialFocusRef={setInitialFocusRef}
			/>
		),
		[fieldId, contextJql, initialValue, onChange],
	);

	return (
		<Popup
			content={renderContent}
			isOpen={isOpen}
			onClose={onClose}
			placement="bottom-start"
			messageId="jql-builder-basic.ui.jql-editor.field.cascade-select-picker.popup"
			messageType="transactional"
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			trigger={renderTrigger}
		/>
	);
};
